<template>
  <div>
    <!-- Header Section -->
    <div class="row mb-4">
      <div class="col-md-8">
        <div class="d-flex align-items-center">
          <div class="bg-primary-light p-3 rounded-2 me-3">
            <vue-feather type="list" class="d-flex justify-content-center align-content-center" stroke="#652aa3" size="24"></vue-feather>
          </div>
          <h3 class="mb-0 fw-bold">Leave Types</h3>
        </div>
      </div>
      <div class="col-md-4">
        <div class="d-flex justify-content-end">
          <button
            v-if="profile.role !== 'user'"
            class="btn btn-primary shadow-sm"
            @click="openAddModal"
          >
            <vue-feather type="plus" size="18" class="me-1"></vue-feather>
            Add Leave Type
          </button>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <Card cardClasses="shadow-sm border-0 h-100 mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div class="font-size-125 color-gray-600">
              <vue-feather type="list" stroke="#652aa3" size="18" class="me-1"></vue-feather>
              Leave Types ({{leaveTypes.length}})
            </div>
            <UInput v-model="dataTableSearch" size="md" placeholder="Search leave type" class="mb-0 w-25 search-input"/>
          </div>

          <!-- Loading Spinner -->
          <div v-if="loading" class="text-center py-4">
            <Spinner spinner-class="mb-1" />
          </div>

          <!-- Leave Types Table -->
          <div v-if="!loading && leaveTypes.length > 0" class="table-responsive">
            <table class="table table-hover align-middle">
              <thead class="table-light">
                <tr>
                  <th scope="col" class="fw-semibold">Name</th>
                  <th scope="col" class="fw-semibold">Code</th>
                  <th scope="col" class="fw-semibold">Default Days</th>
                  <th scope="col" class="fw-semibold">Paid</th>
                  <th scope="col" class="fw-semibold">Requires Approval</th>
                  <th scope="col" class="fw-semibold">Status</th>
                  <th scope="col" class="fw-semibold">Description</th>
                  <th scope="col" class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(leaveType, index) in filteredLeaveTypes" :key="leaveType.id">
                  <td>{{ leaveType.name }}</td>
                  <td>{{ leaveType.code || '-' }}</td>
                  <td>{{ leaveType.default_days }}</td>
                  <td>
                    <span v-if="leaveType.is_paid" class="badge bg-success">Yes</span>
                    <span v-else class="badge bg-danger">No</span>
                  </td>
                  <td>
                    <span v-if="leaveType.requires_approval" class="badge bg-success">Yes</span>
                    <span v-else class="badge bg-danger">No</span>
                  </td>
                  <td>
                    <span v-if="leaveType.is_active" class="badge bg-success">Active</span>
                    <span v-else class="badge bg-danger">Inactive</span>
                  </td>
                  <td>{{ leaveType.description || '-' }}</td>
                  <td class="text-end">
                    <button class="btn btn-sm btn-outline-primary me-2" @click="editLeaveType(leaveType)">
                      <vue-feather type="edit" size="14"></vue-feather>
                    </button>
                    <button class="btn btn-sm btn-outline-danger" @click="confirmDelete(leaveType)">
                      <vue-feather type="trash" size="14"></vue-feather>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Empty State -->
          <div v-if="!loading && leaveTypes.length === 0" class="text-center py-5">
            <vue-feather type="calendar" size="48" class="mb-3" stroke="#652aa3"></vue-feather>
            <h5>No Leave Types Found</h5>
            <p class="text-muted">Create your first leave type to get started.</p>
            <button class="btn btn-primary" @click="openAddModal">
              <vue-feather type="plus" size="16" class="me-1"></vue-feather>
              Add Leave Type
            </button>
          </div>
        </Card>
      </div>
    </div>

    <!-- Add/Edit Leave Type Modal -->
    <Modal
      :open="showModal"
      @closed="closeModal"
    >
      <div class="modal-header border-0">
        <h5 class="modal-title m-0 d-flex align-items-center">
          {{ isEditing ? 'Edit Leave Type' : 'Add Leave Type' }}
        </h5>
        <button @click="closeModal" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="saveLeaveType">
          <div class="mb-3">
            <label for="name" class="form-label">Name <span class="text-danger">*</span></label>
            <input type="text" class="form-control" id="name" v-model="form.name" required>
          </div>
          <div class="mb-3">
            <label for="code" class="form-label">Code</label>
            <input type="text" class="form-control" id="code" v-model="form.code">
          </div>
          <div class="mb-3">
            <label for="default_days" class="form-label">Default Days <span class="text-danger">*</span></label>
            <input type="number" class="form-control" id="default_days" v-model="form.default_days" min="0" required>
          </div>
          <div class="mb-3 form-check">
            <input type="checkbox" class="form-check-input" id="is_paid" v-model="form.is_paid">
            <label class="form-check-label" for="is_paid">Is Paid</label>
          </div>
          <div class="mb-3 form-check">
            <input type="checkbox" class="form-check-input" id="requires_approval" v-model="form.requires_approval">
            <label class="form-check-label" for="requires_approval">Requires Approval</label>
          </div>
          <div class="mb-3 form-check">
            <input type="checkbox" class="form-check-input" id="is_active" v-model="form.is_active">
            <label class="form-check-label" for="is_active">Is Active</label>
          </div>
          <div class="mb-3">
            <label for="description" class="form-label">Description</label>
            <textarea class="form-control" id="description" v-model="form.description" rows="3"></textarea>
          </div>
          <div class="d-flex justify-content-end">
            <button type="button" class="btn btn-outline-secondary me-2" @click="closeModal">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              {{ isEditing ? 'Update' : 'Save' }}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  </div>
</template>

<script>
import leaveService from '@/api/leaveService';
import {decrypt} from '@/utils/crypto';
import notify from '@/utils/notify';
import VueFeather from 'vue-feather';
import confirmDialog from "@/utils/confirmDialog";

export default {
  name: 'LeaveTypes',
  components: { VueFeather },
  data() {
    return {
      loading: true,
      saving: false,
      leaveTypes: [],
      dataTableSearch: '',
      showModal: false,
      isEditing: false,
      selectedLeaveType: null,
      profile: {},
      form: {
        name: '',
        code: '',
        default_days: 0,
        is_paid: true,
        requires_approval: true,
        is_active: true,
        description: ''
      }
    };
  },
  computed: {
    filteredLeaveTypes() {
      if (!this.dataTableSearch) return this.leaveTypes;

      const search = this.dataTableSearch.toLowerCase();
      return this.leaveTypes.filter(type =>
        type.name.toLowerCase().includes(search) ||
        (type.code && type.code.toLowerCase().includes(search)) ||
        (type.description && type.description.toLowerCase().includes(search))
      );
    }
  },
  methods: {
    confirmDialog,
    async fetchLeaveTypes() {
      try {
        this.loading = true;
        this.leaveTypes = await leaveService.getLeaveTypes();
      } catch (error) {
        notify.alert.error('Failed to fetch leave types');
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    openAddModal() {
      this.isEditing = false;
      this.form = {
        name: '',
        code: '',
        default_days: 0,
        is_paid: true,
        requires_approval: true,
        is_active: true,
        description: ''
      };
      this.showModal = true;
    },
    editLeaveType(leaveType) {
      this.isEditing = true;
      this.selectedLeaveType = leaveType;
      this.form = {
        name: leaveType.name,
        code: leaveType.code || '',
        default_days: leaveType.default_days,
        is_paid: leaveType.is_paid,
        requires_approval: leaveType.requires_approval,
        is_active: leaveType.is_active,
        description: leaveType.description || ''
      };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    async saveLeaveType() {
      try {
        this.saving = true;

        if (this.isEditing) {
          await leaveService.updateLeaveType(this.selectedLeaveType.id, this.form);
        } else {
          await leaveService.createLeaveType(this.form);
        }

        this.closeModal();
        await this.fetchLeaveTypes();
      } catch (error) {
        notify.alert.error(error.response?.data?.message || 'Failed to save leave type');
        console.error(error);
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(leaveType) {
      this.selectedLeaveType = leaveType;
      confirmDialog('Leave Type', () => this.deleteLeaveType())
    },
    async deleteLeaveType() {
      try {
        await leaveService.deleteLeaveType(this.selectedLeaveType.id);
        await this.fetchLeaveTypes();
      } catch (error) {
        notify.alert.error(error.response?.data?.message || 'Failed to delete leave type');
        console.error(error);
      }
    }
  },
  created() {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));
    this.fetchLeaveTypes();
  }
};
</script>
